/* tslint:disable:jsx-no-lambda */
import autobind from "autobind-decorator";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { Helmet } from "react-helmet";
import ProgressiveImage from "react-progressive-image-loading";

import { Dialog, WithStyles } from "@material-ui/core";
import { StyleRules, Theme, withStyles } from "@material-ui/core/styles";

import { Button, CheckoutDialog, FadeIn, FS, MailingList, promo, Quotes, Resources, RowList, Text } from "../components";

import Intro from "./Intro";
import Screens from "./Screens";
import Section from "./Section";

import blurredBG from "./elements-blurred.jpg";
import developmentPreview from "./elements-preview.jpg";
import development from "./elements.jpg";
import expoPreview from "./expo-preview.jpg"
import expo from "./expo.jpg";
import sketchPreview from "./sketch-preview.jpg";
import sketch from "./sketch.jpg";

type ClassNames = "intro" | "header" | "left" | "right" | "icon" | "sketch" | "link" | "buyNow" | "wLink" | "gutterTop"
 | "iframe" | "paypal";

const styles = (theme: Theme): StyleRules<ClassNames> => ({
    intro: {
        backgroundColor: "#fafafa"
    },
    header: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column-reverse"
        }
    },
    left: {
        width: "50vw",
        paddingLeft: theme.spacing.unit * 4,
        paddingRight: theme.spacing.unit * 4,
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            textAlign: "center",
            marginBottom: theme.spacing.unit,
            paddingLeft: theme.spacing.unit,
            paddingRight: theme.spacing.unit,
            background: `linear-gradient(rgba(255, 255, 255, .8), rgba(255, 255, 255, .8)), url(${blurredBG})`,
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            display: "flex",
            flex: 1,
            flexDirection: "column",
            justifyContent: "center",
        }
    },
    right: {
        height: "100vh",
        width: "50vw",
        backgroundPosition: "left center",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        [theme.breakpoints.down("sm")]: {
            display: "none"
        }
    },
    icon: {
        marginRight: theme.spacing.unit
    },
    sketch: {
        backgroundImage: `url(${sketch})`
    },
    link: {
        color: theme.palette.primary[500]
    },
    wLink: {
        color: "white"
    },
    paypal: {
        color: "white",
        textDecoration: "none"
    },
    gutterTop: {
        marginTop: theme.spacing.unit * 2
    },
    buyNow: {
        backgroundColor: theme.palette.primary[500],
        color: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: theme.spacing.unit * 8
    },
    iframe: {
        width: 960,
        height: 540,
        [theme.breakpoints.down("sm")]: {
            width: 600,
            height: 362.5
        },
        [theme.breakpoints.down("xs")]: {
            width: 300,
            height: 181.25
        }
    }
});

interface IntroType {
    start: () => void
}

@observer
class Elements extends React.Component<WithStyles<ClassNames>> {

    public intro: IntroType | null;

    @observable public _checkout: boolean = false;
    @observable public _playVideo: boolean = false;

    @computed get checkout() { return this._checkout; }
    set checkout(checkout: boolean) { this._checkout = checkout; }

    @computed get playVideo() { return this._playVideo; }
    set playVideo(playVideo: boolean) { this._playVideo = playVideo; }

    @autobind
    public setIntro(intro: IntroType | null) {
        this.intro = intro;
    }

    @autobind
    public startIntro() {
        if (this.intro) {
            this.intro.start();
        }
    }

    public render() {
        const {classes} = this.props;
        return (
            <div>
                <Helmet>
                    <title>React Native Sketch Elements</title>
                    <meta property="og:title" content="React Native Elements" />
                    <meta property="og:image" content={development} />
                    <meta property="og:description" content="The most comprehensive React Native starter kit" />
                    <meta property="og:image:type" content="image/jpeg" />
                    <meta property="og:image:width" content="1558" />
                    <meta property="og:image:height" content="1440" />
                </Helmet>
                <FS className={classes.header}>
                    <div className={classes.left}>
                        <div>
                            <Text variant="title1">
                            Build your best app
                            </Text>
                            <Text variant="important">
                                Elements is the most comprehensive React Native starter kit.
                                {
                                promo.active && <br />
                                }
                                {
                                promo.active && `Save 75% until ${promo.ends}`
                                }
                            </Text>
                            <RowList>
                                <Button
                                    onClick={() => this.checkout = true}
                                >
                                    {!promo.active ? "$75" : "$19.50"} Buy Now
                                </Button>
                                <Button
                                    href="https://wcandillon.github.io/react-native-elements/BuildingASeedProject.html"
                                    target="_blank"
                                >
                                    Documentation
                                </Button>
                            </RowList>
                        </div>
                    </div>
                    <ProgressiveImage
                        preview={developmentPreview}
                        src={development}
                        render={(src, style) => (
                            <div
                                className={classes.right}
                                style={Object.assign(style, { backgroundImage: `url(${src})` })}
                            />
                        )}
                    />
                </FS>
                <div className={classes.intro}>
                    <FadeIn onVisible={this.startIntro}>
                        <Intro innerRef={this.setIntro} />
                    </FadeIn>
                    <FadeIn>
                        <Quotes />
                    </FadeIn>
                </div>
                <FadeIn>
                    <Screens />
                </FadeIn>
                <FadeIn>
                    <Section
                        uri={expo}
                        preview={expoPreview}
                    >
                        <Text variant="title2">Beautiful, lean and performant</Text>
                        <Text>
Our goal is to provide you with the highest quality codebase possible.
Eslint is heavily used for static analysis, detecting potential bugs and enforcing best practises.
On top of that each component is strictly typed using Flow, thus enabling you to use these components with confidence.
                        </Text>
                        <Text>
To maintain a minimal amount of dependencies and enable you to have fast development cycles,
elements mainly relies on Expo.
Elements also has strict performance requirements.
For instance, all the animations and complexes gestures available are strictly built using the native driver
from React Native.
                        </Text>
                        <div>
                            <Button
                                href="https://wcandillon.github.io/react-native-elements/Packages.html"
                                target="_blank"
                            >
                            View Manifest
                            </Button>
                        </div>
                    </Section>
                </FadeIn>
                <FadeIn>
                    <Section
                        uri={sketch}
                        preview={sketchPreview}
                        reverse={true}
                    >
                        <Text variant="title2">
                        Endless customizations
                        </Text>
                        <Text>
    Sketch Elements is a fascinating UI kit designed by Sketch.
    We have meticulously mapped all symbols from Sketch Elements to React Components.
    As a result, you can quickly build a prototype using Sketch.
    From there, the translation into React Native is trivial.
                        </Text>
                        <Text>
    Each component is fully functional for iOS and Android and is designed using a strict design system
    that can easily be customized to meet your own requirements.
                        </Text>
                        <div>
                            <Button href="https://sketchapp.com/elements" target="_blank">
                            Download Sketch File
                            </Button>
                        </div>
                    </Section>
                </FadeIn>
                <div className={classes.buyNow}>
                    <Text variant="title2" align="center" color="inherit">
                    Get React Native Sketch Elements
                    </Text>
                    {
                        promo.active && (
                            <Text align="center" color="inherit">
                                Save 75% until {promo.ends} - regular price: $75
                            </Text>
                        )
                    }
                    <Button
                        contrast={true}
                        onClick={() => this.checkout = true}
                    >
                        {!promo.active ? "$75" : "$19.50"} Buy Now
                    </Button>
                    <Text align="center" variant="footnote" color="inherit">
<a href="https://wcandillon.github.io/react-native-elements/License.html" target="_blank" className={classes.wLink}>
See license agreement.
</a>
<br />
After checkout, you will get Elements as a single package and access to its Github repository.
                    <br />
If you have any questions,
 please <a href="mailto:wcandillon@gmail.com" className={classes.wLink}>contact us</a>.
                    </Text>
                </div>

                <Resources />

                <MailingList />

                <CheckoutDialog open={this.checkout} onClose={() => this.checkout = false} />

                <Dialog open={this.playVideo} maxWidth={false} onClose={() => this.playVideo = false}>
                    <iframe
                        className={classes.iframe}
                        src="https://www.youtube.com/embed/g13hxWfSisY?autoplay=true&rel=0&showinfo=0"
                        {...{frameBorder: "0", allow: "autoplay; encrypted-media", allowFullScreen: true }}
                    />
                </Dialog>
            </div>
        );
    }
}

export default withStyles(styles)(Elements);



// WEBPACK FOOTER //
// ./src/elements/Elements.tsx